import React from "react";
import {
  Box,
  Grid,
  Typography,
  Paper,
  Chip,
  LinearProgress,
  useTheme,
} from "@mui/material";
import StandardButton from "../../../../../components/StandardButton";
import {
  People as PeopleIcon,
  Rocket as RocketIcon,
  Star as StarIcon,
  Timeline as TimelineIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

/**
 * Motivational banner component for new organizations that need more data collection
 * Shows progress, timeline estimates, and actionable next steps
 */
const DataCollectionBanner = ({
  activeClients = 0,
  customMessage,
  primaryAction = "Add More Clients",
  primaryActionTarget = "/home/clients",
  showProgress = true,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const daysNeeded = Math.max(7, 14 - Math.floor(activeClients * 2)); // Estimate based on client count
  const progress = Math.min(100, (activeClients / 5) * 100); // Progress bar based on having 5+ active clients

  const handlePrimaryAction = () => {
    navigate(primaryActionTarget);
  };

  const defaultMessage = `Your dashboard is taking shape! We need about ${daysNeeded} days of client activity to 
    generate meaningful insights. Keep onboarding clients and encouraging engagement—the more data 
    flows in, the more powerful your analytics become!`;

  return (
    <Paper
      elevation={0}
      sx={{
        background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}10 100%)`,
        border: `1px solid ${theme.palette.primary.light}30`,
        borderRadius: 2,
        p: 3,
        mb: 3,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background decoration */}
      <Box
        sx={{
          position: "absolute",
          top: -50,
          right: -50,
          width: 150,
          height: 150,
          borderRadius: "50%",
          background: `${theme.palette.primary.main}08`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <RocketIcon
          sx={{ fontSize: 60, color: `${theme.palette.primary.main}20` }}
        />
      </Box>

      <Grid container spacing={3} alignItems="center">
        <Grid item xs={12} md={8}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <TimelineIcon sx={{ color: theme.palette.primary.main, mr: 1 }} />
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: theme.palette.text.primary }}
            >
              🚀 Building Your Coaching Analytics Dashboard
            </Typography>
          </Box>

          <Typography
            variant="body1"
            sx={{ mb: 2, color: theme.palette.text.secondary, lineHeight: 1.6 }}
          >
            {customMessage || defaultMessage}
          </Typography>

          {showProgress && (
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Active & Onboarded Client Progress
                </Typography>
                <Typography variant="body2" color="primary">
                  {activeClients}/5 clients
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: `${theme.palette.primary.main}20`,
                  "& .MuiLinearProgress-bar": {
                    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  },
                }}
              />
            </Box>
          )}

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Chip
              icon={<StarIcon />}
              label={`${activeClients} Active & Onboarded`}
              color="primary"
              variant="outlined"
              size="small"
            />
            <Chip
              icon={<TimelineIcon />}
              label={`${daysNeeded} days to insights`}
              color="secondary"
              variant="outlined"
              size="small"
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Ready to accelerate your data collection?
            </Typography>
            <StandardButton
              variant="primary"
              startIcon={<PeopleIcon />}
              onClick={handlePrimaryAction}
              size="large"
            >
              {primaryAction}
            </StandardButton>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default DataCollectionBanner;
